// File: frontend/src/pages/FilmRoomPage.tsx
//
// Standalone Film Room page: chapter routing (/film-room and /film-room/:slug)
// around the shared FilmRoomChapterView. The same experience is embedded on
// the How to Play page via FilmRoomSection.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import { mixpanelTrack } from '@/services/mixpanel';
import FilmRoomChapterView from '@/components/scoring/FilmRoomChapterView';
import FilmRoomSelector from '@/components/scoring/FilmRoomSelector';
import {
  FilmRoomChapter,
  chapterLeague,
  chaptersForLeague,
  getChapter,
} from '@/data/filmRoomChapters';

const chapterPath = (slug: string): string =>
  slug === 'yamal' ? '/film-room' : `/film-room/${slug}`;

const FilmRoomPage: React.FC = () => {
  const { chapterSlug } = useParams<{ chapterSlug: string }>();
  const navigate = useNavigate();
  const chapter: FilmRoomChapter = getChapter(chapterSlug);
  // League selection navigates to that league's first chapter so the visible
  // controls and video content always describe the same competition.
  const [leagueSlug, setLeagueSlug] = useState(chapterLeague(chapter));

  useEffect(() => {
    setLeagueSlug(chapterLeague(chapter));
  }, [chapter]);

  const handleLeagueSelect = (slug: string) => {
    if (slug === leagueSlug) return;
    const firstChapter = chaptersForLeague(slug)[0];
    if (firstChapter) {
      navigate(chapterPath(firstChapter.slug));
    } else {
      setLeagueSlug(slug);
    }
  };

  useEffect(() => {
    mixpanelTrack('Viewed Film Room', {
      Chapter: chapter.slug,
      Match: `${chapter.match.homeTeam} ${chapter.match.homeGoals}-${chapter.match.awayGoals} ${chapter.match.awayTeam}`,
    });
  }, [chapter]);

  return (
    <div className="min-h-screen bg-primary text-gray-200 font-body">
      <SEO
        title={`Film Room - ${chapter.player.nickname}`}
        description={`Follow ${chapter.player.nickname}'s full scoring log from ${chapter.match.homeTeam} ${chapter.match.homeGoals}-${chapter.match.awayGoals} ${chapter.match.awayTeam}, synced to the official match video.`}
        path={chapterPath(chapter.slug)}
      />
      <div className="py-6 md:py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="mb-4 md:mb-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold uppercase tracking-wider text-white">
              Film Room
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {chapter.player.nickname} - {chapter.match.homeTeam} {chapter.match.homeGoals}-
              {chapter.match.awayGoals} {chapter.match.awayTeam}, {chapter.match.competition}
            </p>
            <div className="mt-4">
              <FilmRoomSelector
                leagueSlug={leagueSlug}
                activeChapterSlug={chapter.slug}
                onLeagueSelect={handleLeagueSelect}
                chapterHref={chapterPath}
                align="start"
              />
            </div>
          </div>
          <FilmRoomChapterView chapter={chapter} surface="film-room-page" />
        </div>
      </div>
    </div>
  );
};

export default FilmRoomPage;
